import React from "react";
import {Button, Modal, Spinner} from "react-bootstrap";
import Moment from "react-moment";
import {NumericFormat as NumberFormat} from "react-number-format";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {productoActions} from "../../_actions";
import {TIPO_ENVIO, TIPO_REPUESTO} from "../../_helpers";
import productoServiceTs from "../../_services/cart/producto.service";

/**
 * El resumen de una publicacion, para mirarla sin entrar a editarla.
 *
 * <h3>Pinta primero y pide despues</h3>
 * La fila del listado ya trae casi todo (viene de {@code ProductoDto.ofDetail}), asi que el
 * resumen se pinta con eso apenas se abre y no hay que esperar a la red para leerlo. Lo unico
 * que falta ahi son las imagenes —{@code ofDetail} no las carga, solo la variante publica— y
 * eso se pide aparte con {@code cargarDetalle}: llega despues y se agrega arriba.
 *
 * <p>Se le pasa {@code cargarDetalle} en vez de que el modal elija el endpoint porque el
 * carril no es el mismo en los dos listados: el vendedor solo puede pedir las suyas
 * (/api/vendedor/...) y el back office las de cualquiera (/api/admin/...).
 */
class ResumenPublicacionModal extends React.Component {
    state = {
        detalle: null,
        cargandoDetalle: false,
    };

    componentDidMount() {
        this.pedirDetalle();
    }

    componentDidUpdate(prevProps) {
        const idAnterior = prevProps.producto && prevProps.producto.id;
        const idActual = this.props.producto && this.props.producto.id;

        if (idAnterior !== idActual) {
            this.setState({detalle: null});
            this.pedirDetalle();
        }
    }

    pedirDetalle = () => {
        const {producto, cargarDetalle} = this.props;

        if (!producto || !cargarDetalle) return;

        this.setState({cargandoDetalle: true});

        cargarDetalle(producto.id).then(
            (data) => {
                this.setState({detalle: (data && data.producto) || null, cargandoDetalle: false});
            },
            () => {
                // El resumen se lee igual sin la foto: si el detalle falla no se muestra un
                // error encima, simplemente no aparece la imagen.
                this.setState({cargandoDetalle: false});
            }
        );
    };

    imagen = () => {
        const {detalle} = this.state;
        const imagenes = (detalle && detalle.imagenes) || [];

        return imagenes.length > 0 ? imagenes[0].url : null;
    };

    render() {
        const {producto, onHide, onEditar} = this.props;
        const {cargandoDetalle} = this.state;

        if (!producto) return null;

        const url = this.imagen();
        const vehiculo = [
            producto.marca && producto.marca.nombre,
            producto.modelo && producto.modelo.nombre,
            producto.version && producto.version.nombre,
            producto.anio,
        ]
            .filter(Boolean)
            .join(" · ");

        return (
            <Modal size="lg" show={true} onHide={onHide} className="resumen-publicacion">
                <Modal.Header closeButton>
                    <Modal.Title>Publicación #{producto.id}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="resumen-publicacion__cabecera">
                        <div className="resumen-publicacion__foto">
                            {url && <img src={url} alt={"Foto de la publicación " + producto.id}/>}
                            {!url && cargandoDetalle && <Spinner animation="border" size="sm"/>}
                            {!url && !cargandoDetalle && (
                                <FontAwesomeIcon icon="fa-solid fa-image" size="2x"/>
                            )}
                        </div>

                        <div className="resumen-publicacion__titulo">
                            <h5>
                                {producto.tipoRepuesto && producto.tipoRepuesto.nombre}
                                {producto.tipoRepuesto &&
                                    producto.tipoRepuesto.nombre === TIPO_REPUESTO.MOTOR_04 && (
                                        <small> ({productoServiceTs.crearNombreMotorAsociados(producto)})</small>
                                    )}
                            </h5>
                            <p>{vehiculo}</p>
                            <p className="resumen-publicacion__precio">
                                {productoActions.esPrecioAConsultar(producto) ? (
                                    "A consultar"
                                ) : (
                                    <NumberFormat
                                        prefix="$"
                                        value={producto.precio}
                                        displayType={"text"}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                    />
                                )}
                            </p>
                        </div>
                    </div>

                    <dl className="resumen-publicacion__datos">
                        <div>
                            <dt>Estado</dt>
                            <dd>{producto.estado}</dd>
                        </div>
                        <div>
                            <dt>Cantidad</dt>
                            <dd>{producto.cantidad}</dd>
                        </div>
                        <div>
                            <dt>Envío</dt>
                            <dd>
                                {producto.tipoEnvio === TIPO_ENVIO.ENVIO_A_COORDINAR
                                    ? "A coordinar"
                                    : "Sin envío"}
                            </dd>
                        </div>
                        {!!producto.kilometraje && (
                            <div>
                                <dt>Kilometraje</dt>
                                <dd>
                                    <NumberFormat
                                        value={producto.kilometraje}
                                        displayType={"text"}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        suffix=" km"
                                    />
                                </dd>
                            </div>
                        )}
                        {!!producto.createdDate && (
                            <div>
                                <dt>Alta</dt>
                                <dd>
                                    <Moment date={producto.createdDate} format="DD/MM/YYYY HH:mm"/>
                                </dd>
                            </div>
                        )}
                        {/* Solo en el back office: en el listado propio el vendedor es siempre uno. */}
                        {!!producto.vendedor && (
                            <div>
                                <dt>Vendedor</dt>
                                <dd>{producto.vendedor.username}</dd>
                            </div>
                        )}
                    </dl>

                    {!!producto.descripcion && (
                        <div className="resumen-publicacion__texto">
                            <h6>Descripción</h6>
                            <p>{producto.descripcion}</p>
                        </div>
                    )}

                    {/* La nota interna no sale en el sitio publico, por eso va marcada. */}
                    {!!producto.notaInterna && (
                        <div className="resumen-publicacion__texto">
                            <h6>Nota interna (no se publica)</h6>
                            <p>{producto.notaInterna}</p>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button className="button" data-type="complementary" onClick={onHide}>
                        Cerrar
                    </Button>
                    <Button
                        className="button"
                        variant="primary"
                        onClick={() => {
                            onHide();
                            onEditar(producto);
                        }}
                    >
                        <FontAwesomeIcon icon="fa-solid fa-pen"/> Editar
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export {ResumenPublicacionModal};
